import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    Phone,
    Activity,
    CheckCircle,
    Clock,
    BarChart3,
    Workflow,
    AlertCircle,
    CheckCircle2,
    MessageSquare,
    ShieldCheck,
    Headphones,
    CalendarCheck,
    Banknote,
} from "lucide-react";
import {
    Card,
    CardHeader,
    CardTitle,
    CardContent,
} from "@component/card";
import { formatDuration, statusBadgeConfig, chipVariantStyles, chipDotColor } from "@util/agent-scoring";
import { cn } from "@util/tailwind";
import { format, subDays } from "date-fns";
import { USE_CASE_OPTIONS } from "@type/agent";
import { formatMYT } from "@util/date";
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    ResponsiveContainer,
} from "recharts";
import {
    getAgentDashboardStats,
    getAgentDashboardChart,
    getAgentCallSessions,
    getAgentCampaigns,
} from "@service/agent";
import type {
    DashboardStats,
    DashboardChartResponse,
    CallSession,
    CampaignListItem,
} from "@type/agent";

type DateRange = "7d" | "30d" | "90d" | "all";
const dateRangeOptions: { value: DateRange; label: string }[] = [
    { value: "7d", label: "Last 7 days" },
    { value: "30d", label: "Last 30 days" },
    { value: "90d", label: "Last 90 days" },
    { value: "all", label: "All-time" },
];

const useCaseIcons: Record<string, { icon: React.ComponentType<{ className?: string }> }> = {
    lead_qualification: { icon: CheckCircle2 },
    survey: { icon: MessageSquare },
    customer_verification: { icon: ShieldCheck },
    support_automation: { icon: Headphones },
    appointment_scheduling: { icon: CalendarCheck },
    collections: { icon: Banknote },
    custom: { icon: Workflow },
};

export default function AgentDashboard() {
    const navigate = useNavigate();
    const [dashboardStats, setDashboardStats] = useState<DashboardStats | null>(null);
    const [chartData, setChartData] = useState<DashboardChartResponse | null>(null);
    const [recentSessions, setRecentSessions] = useState<CallSession[]>([]);
    const [activeCampaigns, setActiveCampaigns] = useState<CampaignListItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [dateRange, setDateRange] = useState<DateRange>("7d");

    useEffect(() => {
        async function load() {
            try {
                setLoading(true);
                const dateAfter = dateRange !== "all"
                    ? format(subDays(new Date(), dateRange === "7d" ? 7 : dateRange === "30d" ? 30 : 90), "yyyy-MM-dd")
                    : undefined;
                const [stats, chart, sessionsRes, campaigns] = await Promise.all([
                    getAgentDashboardStats(dateRange),
                    getAgentDashboardChart(dateRange),
                    getAgentCallSessions({ limit: 10, dateAfter }),
                    getAgentCampaigns("active"),
                ]);
                setDashboardStats(stats);
                setChartData(chart);
                setRecentSessions(sessionsRes.sessions);
                setActiveCampaigns(campaigns);
            } catch (err) {
                setError(err instanceof Error ? err.message : "Failed to load dashboard");
            } finally {
                setLoading(false);
            }
        }
        load();
    }, [dateRange]);

    if (loading) {
        return (
            <div className="space-y-6">
                {/* Header — kept interactive so the user can re-pick a range
                    without waiting for the spinner to clear. */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Dashboard</h1>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Track call activity, campaign progress, and performance metrics.</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <span className="text-sm text-gray-500">Showing metrics for:</span>
                        <select
                            value={dateRange}
                            onChange={(e) => setDateRange(e.target.value as DateRange)}
                            className="appearance-none px-3 py-1.5 pr-8 text-sm font-medium border border-gray-200 rounded-lg bg-white text-gray-700 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent dark:bg-gray-900 dark:border-gray-700 dark:text-gray-300"
                        >
                            {dateRangeOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Stat card skeletons (6) */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    {Array.from({ length: 6 }).map((_, i) => (
                        <div key={i} className="rounded-lg border bg-white dark:bg-gray-900 shadow-sm p-5">
                            <div className="flex items-start gap-3">
                                <div className="w-9 h-9 rounded-full bg-gray-100 dark:bg-gray-800 animate-pulse" />
                                <div className="flex-1 space-y-2">
                                    <div className="h-3 w-20 bg-gray-100 dark:bg-gray-800 rounded animate-pulse" />
                                    <div className="h-6 w-12 bg-gray-100 dark:bg-gray-800 rounded animate-pulse" />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Chart skeletons (2) */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {Array.from({ length: 2 }).map((_, i) => (
                        <div key={i} className="rounded-lg border bg-white dark:bg-gray-900 shadow-sm p-5">
                            <div className="h-4 w-32 bg-gray-100 dark:bg-gray-800 rounded animate-pulse mb-5" />
                            <div className="h-[280px] bg-gray-50 dark:bg-gray-800/50 rounded animate-pulse" />
                        </div>
                    ))}
                </div>

                {/* Active campaigns + recent activity table skeletons */}
                {Array.from({ length: 2 }).map((_, i) => (
                    <div key={i} className="rounded-lg border bg-white dark:bg-gray-900 shadow-sm p-5">
                        <div className="h-4 w-40 bg-gray-100 dark:bg-gray-800 rounded animate-pulse mb-5" />
                        <div className="space-y-3">
                            {Array.from({ length: 4 }).map((__, r) => (
                                <div key={r} className="flex items-center gap-3">
                                    <div className="h-4 w-6 bg-gray-100 dark:bg-gray-800 rounded animate-pulse" />
                                    <div className="h-4 flex-1 bg-gray-100 dark:bg-gray-800 rounded animate-pulse" />
                                    <div className="h-4 w-24 bg-gray-100 dark:bg-gray-800 rounded animate-pulse" />
                                    <div className="h-4 w-20 bg-gray-100 dark:bg-gray-800 rounded animate-pulse" />
                                    <div className="h-4 w-16 bg-gray-100 dark:bg-gray-800 rounded animate-pulse" />
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center gap-2 p-4 bg-red-50 text-red-700 rounded-lg">
                <AlertCircle className="w-5 h-5" />
                <span>{error}</span>
            </div>
        );
    }

    if (!dashboardStats) return null;

    const stats = [
        { name: "Total Campaigns", value: dashboardStats.totalCampaigns, icon: Workflow, tooltip: "Total number of campaigns created" },
        { name: "Active Campaigns", value: dashboardStats.activeCampaigns, icon: Activity, tooltip: "Campaigns currently running and making calls" },
        { name: "Total Calls", value: dashboardStats.totalCalls, icon: Phone, tooltip: "Total calls made across all campaigns" },
        { name: "Completed", value: dashboardStats.completedCalls, icon: CheckCircle, tooltip: "Calls that finished successfully" },
        { name: "Avg Duration", value: formatDuration(dashboardStats.avgDurationSeconds), icon: Clock, tooltip: "Average call duration across all completed calls" },
        // Cap avg score to 2 decimal places so BE-side floats like
        // 60.5500000000001 don't leak full precision into the UI. Trailing
        // zeros get dropped automatically by Number coercion (61 → "61",
        // 60.5 → "60.5", 60.55 → "60.55").
        {
            name: "Avg Score",
            value: `${Number.isFinite(dashboardStats.avgScore) ? Math.round(dashboardStats.avgScore * 100) / 100 : 0}%`,
            icon: BarChart3,
            tooltip: "Average evaluation score across all scored calls",
        },
    ];

    // Chart data for recharts
    const callsChartData = (chartData?.dataPoints || []).map((p) => ({
        date: p.date,
        calls: p.totalCalls,
    }));

    const successRateChartData = (chartData?.dataPoints || []).map((p) => ({
        date: p.date,
        total: p.totalCalls,
        success: p.successCalls,
        failure: p.failedCalls,
        rate: p.totalCalls > 0 ? Math.round((p.successCalls / p.totalCalls) * 1000) / 10 : 0,
    }));

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Dashboard</h1>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Track call activity, campaign progress, and performance metrics.</p>
                </div>
                <div className="flex items-center gap-3">
                    <span className="text-sm text-gray-500">Showing metrics for:</span>
                    <select
                        value={dateRange}
                        onChange={(e) => setDateRange(e.target.value as DateRange)}
                        className="appearance-none px-3 py-1.5 pr-8 text-sm font-medium border border-gray-200 rounded-lg bg-white text-gray-700 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent dark:bg-gray-900 dark:border-gray-700 dark:text-gray-300"
                    >
                        {dateRangeOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Stat Cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {stats.map((stat) => (
                    <div key={stat.name} className="rounded-lg border bg-white dark:bg-gray-900 shadow-sm p-5" title={stat.tooltip}>
                        <div className="flex items-start gap-3">
                            <div className="p-2 rounded-full bg-blue-50 dark:bg-blue-900/30">
                                <stat.icon className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{stat.name}</p>
                                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{stat.value}</p>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Number of Calls Chart */}
                <Card>
                    <CardHeader>
                        <CardTitle>Number of Calls</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {callsChartData.length > 0 ? (
                            <div className="h-[280px]">
                                <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart data={callsChartData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                                        <defs>
                                            <linearGradient id="callsGradient" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.2} />
                                                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                                        <XAxis
                                            dataKey="date"
                                            tick={{ fontSize: 11, fill: "#9ca3af" }}
                                            tickFormatter={(v) => format(new Date(v), "MMM d")}
                                            axisLine={false}
                                            tickLine={false}
                                        />
                                        <YAxis
                                            tick={{ fontSize: 11, fill: "#9ca3af" }}
                                            axisLine={false}
                                            tickLine={false}
                                            allowDecimals={false}
                                        />
                                        <RechartsTooltip
                                            content={({ active, payload, label }) => {
                                                if (!active || !payload?.length) return null;
                                                return (
                                                    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg px-3 py-2 text-sm">
                                                        <p className="font-medium text-gray-900 dark:text-white">{label != null ? format(new Date(label), "MMM d, yyyy") : ""}</p>
                                                        <p className="text-blue-600">{payload[0].value} calls</p>
                                                    </div>
                                                );
                                            }}
                                        />
                                        <Area
                                            type="monotone"
                                            dataKey="calls"
                                            stroke="#3b82f6"
                                            strokeWidth={2}
                                            fill="url(#callsGradient)"
                                        />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                        ) : (
                            <div className="flex items-center justify-center h-[280px] text-gray-400">
                                <p className="text-sm">No data available.</p>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Overall Success Rate Chart */}
                <Card>
                    <CardHeader className="flex-row items-center justify-between">
                        <div>
                            <CardTitle>Overall Success Rate</CardTitle>
                            <p className="text-2xl font-semibold text-gray-900 dark:text-white mt-1">
                                {chartData?.overallSuccessRate != null
                                    ? `${chartData.overallSuccessRate.toFixed(1)}%`
                                    : "--"}
                            </p>
                        </div>
                    </CardHeader>
                    <CardContent>
                        {successRateChartData.length > 0 ? (
                            <div className="h-[240px]">
                                <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart data={successRateChartData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                                        <defs>
                                            <linearGradient id="successGradient" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#22c55e" stopOpacity={0.2} />
                                                <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                                            </linearGradient>
                                            <linearGradient id="totalGradient" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#9ca3af" stopOpacity={0.1} />
                                                <stop offset="95%" stopColor="#9ca3af" stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                                        <XAxis
                                            dataKey="date"
                                            tick={{ fontSize: 11, fill: "#9ca3af" }}
                                            tickFormatter={(v) => format(new Date(v), "MMM d")}
                                            axisLine={false}
                                            tickLine={false}
                                        />
                                        <YAxis
                                            tick={{ fontSize: 11, fill: "#9ca3af" }}
                                            axisLine={false}
                                            tickLine={false}
                                            domain={[0, 100]}
                                            tickFormatter={(v) => `${v}%`}
                                        />
                                        <RechartsTooltip
                                            content={({ active, payload, label }) => {
                                                if (!active || !payload?.length) return null;
                                                const d = payload[0]?.payload;
                                                if (!d) return null;
                                                const successPct = d.total > 0 ? ((d.success / d.total) * 100).toFixed(1) : "0.0";
                                                const failurePct = d.total > 0 ? ((d.failure / d.total) * 100).toFixed(1) : "0.0";
                                                return (
                                                    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg px-3 py-2 text-sm">
                                                        <div className="flex items-center justify-between gap-6 mb-1">
                                                            <p className="font-medium text-gray-900 dark:text-white">{label != null ? format(new Date(label), "MMM d, yyyy") : ""}</p>
                                                            <p className="font-semibold text-gray-900 dark:text-white">{d.total}</p>
                                                        </div>
                                                        <div className="flex items-center gap-2 text-green-600">
                                                            <span className="w-2 h-2 rounded-full bg-green-500 inline-block" />
                                                            Success: {d.success} ({successPct}%)
                                                        </div>
                                                        <div className="flex items-center gap-2 text-red-500">
                                                            <span className="w-2 h-2 rounded-full bg-red-500 inline-block" />
                                                            Failure: {d.failure} ({failurePct}%)
                                                        </div>
                                                    </div>
                                                );
                                            }}
                                        />
                                        <Area
                                            type="monotone"
                                            dataKey="rate"
                                            stroke="#22c55e"
                                            strokeWidth={2}
                                            fill="url(#successGradient)"
                                        />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                        ) : (
                            <div className="flex items-center justify-center h-[240px] text-gray-400">
                                <p className="text-sm">No data available.</p>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Active Campaigns */}
            <div>
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2.5">
                        <span className="font-semibold leading-none tracking-tight text-gray-900 dark:text-white">Active Campaigns</span>
                        <span className="text-xs font-semibold font-mono px-2 py-0.5 rounded-[5px] bg-green-50 text-green-600">
                            {activeCampaigns.length}
                        </span>
                    </div>
                    <Link to="/agent/call-setup" className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:opacity-80">
                        View all
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M5 3l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </Link>
                </div>
                <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
                    {activeCampaigns.length > 0 ? (
                        <>
                            {/* Header row */}
                            <div
                                className="grid bg-[#fafbfd] dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700 px-4 py-2.5"
                                style={{ gridTemplateColumns: "50px minmax(180px, 300px) 0.8fr 1.3fr 0.7fr 0.7fr 1fr 0.7fr" }}
                            >
                                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">No</span>
                                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Campaign</span>
                                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Campaign ID</span>
                                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Use Case</span>
                                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider text-center">Calls</span>
                                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider text-center">Contacts</span>
                                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider text-center">Completion</span>
                                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider text-center">Avg Score</span>
                            </div>
                            {/* Data rows */}
                            {activeCampaigns.map((campaign, index) => {
                                const UseCaseIcon = useCaseIcons[campaign.useCase]?.icon || Workflow;
                                const completionPct = campaign.contactCount > 0
                                    ? Math.min(100, Math.round((campaign.sessionCount / campaign.contactCount) * 100))
                                    : 0;
                                return (
                                    <Link
                                        key={campaign.id}
                                        to={`/agent/call-setup/${campaign.id}`}
                                        className="grid items-center px-4 py-3 border-b border-gray-100 dark:border-gray-800 last:border-b-0 hover:bg-[#f8f9fd] dark:hover:bg-gray-800/70 cursor-pointer transition-[background] duration-100"
                                        style={{ gridTemplateColumns: "50px minmax(180px, 300px) 0.8fr 1.3fr 0.7fr 0.7fr 1fr 0.7fr" }}
                                    >
                                        {/* No */}
                                        <span className="text-sm text-gray-500 dark:text-gray-400">
                                            {index + 1}
                                        </span>
                                        {/* Campaign */}
                                        <div className="flex items-center gap-2.5 min-w-0">
                                            <div className="w-8 h-8 shrink-0 bg-blue-50 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                                                <UseCaseIcon className="w-4 h-4 text-blue-600" />
                                            </div>
                                            <span className="text-sm font-medium text-gray-900 dark:text-white truncate">{campaign.name}</span>
                                        </div>
                                        {/* Campaign ID */}
                                        <span className="text-sm text-gray-500 dark:text-gray-400 font-mono truncate">
                                            {campaign.id.slice(0, 8)}
                                        </span>
                                        {/* Use Case */}
                                        <span className="text-sm text-gray-700 dark:text-gray-300 truncate">
                                            {USE_CASE_OPTIONS[campaign.useCase]?.label || campaign.useCase.replace(/_/g, " ")}
                                        </span>
                                        {/* Calls */}
                                        <span className={cn("text-sm text-center", campaign.sessionCount === 0 ? "text-gray-400" : "text-gray-700 dark:text-gray-300")}>
                                            {campaign.sessionCount}
                                        </span>
                                        {/* Contacts */}
                                        <span className={cn("text-sm text-center", campaign.contactCount === 0 ? "text-gray-400" : "text-gray-700 dark:text-gray-300")}>
                                            {campaign.contactCount}
                                        </span>
                                        {/* Completion */}
                                        <span className={cn("text-sm text-center", completionPct === 0 ? "text-gray-400" : "text-gray-700 dark:text-gray-300")}>
                                            {completionPct}%
                                        </span>
                                        {/* Avg Score */}
                                        <span className={cn("text-sm text-center", campaign.successRate == null ? "text-gray-400" : "text-gray-700 dark:text-gray-300")}>
                                            {campaign.successRate != null ? `${Math.round(campaign.successRate)}%` : "\u2014"}
                                        </span>
                                    </Link>
                                );
                            })}
                        </>
                    ) : (
                        <div className="flex flex-col items-center justify-center py-10 text-gray-400">
                            <Workflow className="w-8 h-8 mb-2" />
                            <p className="text-sm">No active campaigns.</p>
                            <Link to="/agent/call-setup/new" className="text-xs text-blue-600 hover:underline mt-1">Create a campaign to get started</Link>
                        </div>
                    )}
                </div>
            </div>

            {/* Recent Call Activity */}
            <div>
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2.5">
                        <span className="font-semibold leading-none tracking-tight text-gray-900 dark:text-white">Recent Call Activity</span>
                        <span className="text-xs font-semibold font-mono px-2 py-0.5 rounded-[5px] bg-blue-50 text-blue-600">
                            {recentSessions.length}
                        </span>
                    </div>
                    <Link to="/agent/calls" className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:opacity-80">
                        View all
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M5 3l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </Link>
                </div>
                <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden overflow-x-auto">
                    {recentSessions.length > 0 ? (
                        <>
                            {/* Header row */}
                            <div
                                className="grid bg-[#fafbfd] dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700 px-4 py-2.5"
                                style={{ gridTemplateColumns: "50px 1.4fr 1fr 1.4fr 0.65fr 0.7fr 0.7fr 0.42fr 0.7fr" }}
                            >
                                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">No</span>
                                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Contact</span>
                                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Phone #</span>
                                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Campaign</span>
                                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Date & Time</span>
                                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider text-center">Messages</span>
                                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider text-center">Duration</span>
                                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider text-center">Attempt</span>
                                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider text-right">Call Status</span>
                            </div>
                            {/* Data rows */}
                            {recentSessions.map((session, index) => {
                                const config = statusBadgeConfig[session.status] || { label: session.status, variant: "default" as const };
                                const isUnknown = !session.contactName || session.contactName === "Unknown";
                                return (
                                    <div
                                        key={session.id}
                                        onClick={() => navigate(`/agent/calls/${session.id}`)}
                                        className="grid items-center px-4 py-3 border-b border-gray-100 dark:border-gray-800 last:border-b-0 hover:bg-[#f8f9fd] dark:hover:bg-gray-800/70 cursor-pointer transition-[background] duration-100"
                                        style={{ gridTemplateColumns: "50px 1.4fr 1fr 1.4fr 0.65fr 0.7fr 0.7fr 0.42fr 0.7fr" }}
                                    >
                                        {/* No */}
                                        <span className="text-sm text-gray-500 dark:text-gray-400">
                                            {index + 1}
                                        </span>
                                        {/* Contact: avatar + name + call ID */}
                                        <div className="flex items-center gap-2 min-w-0">
                                            <div
                                                className={cn(
                                                    "w-[30px] h-[30px] rounded-[7px] flex-shrink-0 flex items-center justify-center text-xs font-bold",
                                                    isUnknown
                                                        ? "bg-gray-100 dark:bg-gray-800 text-gray-400"
                                                        : "bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400"
                                                )}
                                            >
                                                {isUnknown ? "?" : session.contactName![0].toUpperCase()}
                                            </div>
                                            <div className="min-w-0">
                                                <div className="flex items-center gap-1.5">
                                                    <span className="text-[13px] font-semibold text-gray-900 dark:text-white truncate">
                                                        {session.contactName || "Unknown"}
                                                    </span>
                                                    {session.isTestCall && (
                                                        <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold rounded bg-indigo-50 text-indigo-600 border border-indigo-200 flex-shrink-0">
                                                            Test
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="text-[10.5px] font-mono text-gray-400 mt-px">
                                                    {session.id.slice(0, 8)}
                                                </div>
                                            </div>
                                        </div>
                                        {/* Phone # */}
                                        <span className="text-sm text-gray-500 dark:text-gray-400 truncate">
                                            {session.contactPhone || "--"}
                                        </span>
                                        {/* Campaign */}
                                        <span className="text-sm text-gray-500 dark:text-gray-400 truncate">
                                            {session.campaignName || "--"}
                                        </span>
                                        {/* Date & Time */}
                                        <div>
                                            <div className="text-sm text-gray-700 dark:text-gray-300">
                                                {formatMYT(session.startedAt, "D MMM YYYY")}
                                            </div>
                                            <div className="text-xs text-gray-400 mt-0.5">
                                                {formatMYT(session.startedAt, "h:mm A")}
                                            </div>
                                        </div>
                                        {/* Messages */}
                                        <span className={cn(
                                            "text-sm text-center",
                                            !session.messageCount ? "text-gray-400" : "text-gray-700 dark:text-gray-300"
                                        )}>
                                            {session.messageCount || "\u2014"}
                                        </span>
                                        {/* Duration */}
                                        <span className={cn(
                                            "text-sm text-center",
                                            session.duration === 0 ? "text-gray-400" : "text-gray-700 dark:text-gray-300"
                                        )}>
                                            {formatDuration(session.duration)}
                                        </span>
                                        {/* Attempt */}
                                        <span className="text-sm text-center text-gray-700 dark:text-gray-300">
                                            {(session.retryCount ?? 0) + 1}
                                        </span>
                                        {/* Call Status */}
                                        <div className="flex justify-end">
                                            <span className={cn(
                                                "inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full border",
                                                chipVariantStyles[config.variant]
                                            )}>
                                                <span className={cn("w-1.5 h-1.5 rounded-full", chipDotColor[config.variant])} />
                                                {config.label}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}
                        </>
                    ) : (
                        <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                            <Phone className="w-8 h-8 mb-2" />
                            <p className="text-sm">No recent call activity.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
